// filepath: apps/web/src/features/admin/StagingOnlyError.tsx
//
// FR-UI-ADM-1 AC-2 — full-screen error panel rendered when the
// resolved environmentKind === PRODUCTION.
//
// The panel is the SPA-side mirror of the server-side staging-only
// guard on PUT /api/roles/{roleName}/permissions (HTTP 403
// ENV_PRODUCTION). Defense-in-depth: the server rejects the write
// even if a future SPA bug disables this screen.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";

interface StagingOnlyErrorProps {
  /** Resolved environment kind. Used for the code label only. */
  environmentKind: string;
  /** Optional message (e.g. when triggered by a 403 server reject). */
  message?: string;
}

export function StagingOnlyError({
  environmentKind,
  message,
}: StagingOnlyErrorProps): React.ReactElement {
  const { t } = useTranslation();

  return (
    <div
      className="flex min-h-[60vh] flex-col items-center justify-center p-6 text-center"
      role="alert"
      data-testid="staging-only-error"
    >
      <h2 className="mb-2 text-lg font-semibold">
        {t("admin.errors.staging_only.title")}
      </h2>
      <p className="mb-3 max-w-prose text-sm text-muted-foreground">
        {t("admin.errors.staging_only.body")}
      </p>
      <p className="text-xs text-muted-foreground">
        {t("admin.errors.staging_only.code")}: {environmentKind}
      </p>
      {message !== undefined && (
        <p className="mt-2 text-xs text-muted-foreground">{message}</p>
      )}
    </div>
  );
}
